import { Fragment, useEffect, useState } from "react";
import { FormattedMessage as FM } from "react-intl";
import date from "date-and-time";
import { signal } from "@preact/signals-react";

import funcs from "tools/functions";
import useMainStore from "store/mainStore";
import CalendarFilter from "components/Tools/CalendarFilter";
import PanelRight from "components/Panel/PanelRight";
import WidgetSideStatusActivity from "./components/WidgetSideStatusActivity";
import StdButton from "components/Buttons/StdButton";
import proxy from "api/proxy";
import QuickForm from "components/Meta/QuickForm";
import ScheduledActivities from "./components/ScheduledActivities";
import MenuRest from "./components/MenuRest";
import { getStorage } from "store/local";
import QuickTable from "components/Meta/QuickTable";
import BoardHeader from "components/Meta/BoardHeader";

const dayToday = new Date();
const statusSection = signal(true);
const filterDay = signal(dayToday);
const statusShedule = signal(true);
const view = signal("create");
const prevView = signal("create");

const ScreenActivity = (props) => {
  const { ctxView, ctxViewAc, permits, configuration } = props;

  const [newCtxView] = useState(ctxView);
  const [filterHistory, setFilterHistory] = useState([]);
  const isLastToday = dayToday <= filterDay.value;
  const session = getStorage("ctxSession");
  const saleActivity = getStorage("saleActivity");
  const {
    store: storeRec,
    record,
    setRecord,
    updateRecord,
    updateStore,
  } = useMainStore();

  const handleChangeDate = async (value) => {
    if (!permits) return;
    const _date = new Date(value);
    if (_date < dayToday && view.value === "create") {
      return false;
    }

    filterDay.value = _date;
    await resetAndSetShop(value);

    const newDomain = [
      ["sale_date", "=", date.format(new Date(value), "YYYY-MM-DD")],
      ["shop", "=", parseInt(session.shop, 10)],
    ];
    newCtxView.domain = newDomain;
    const _storeRec = { ...storeRec };
    const _record = { ...record };
    if (record.kind !== "reservation" && !record.lines.size > 0) {
      _storeRec.shipment_date = date.format(value, "YYYY-MM-DD");
      _record.shipment_date = date.format(value, "YYYY-MM-DD");
    }
    updateStore(_storeRec);
    updateRecord(_record);
  };

  const handleViewReservation = (reservation) => {
    const _storeRec = { ...storeRec };
    const _record = { ...record };
    _storeRec.shipment_date = date.format(filterDay.value, "YYYY-MM-DD");
    _record.shipment_date = date.format(filterDay.value, "YYYY-MM-DD");
    updateStore(_storeRec);
    updateRecord(_record);

    const newDomain = [
      ["shipment_date", ">=", date.format(filterDay.value, "YYYY-MM-DD")],
      ["kind", "=", "reservation"],
      ["agent", "=", saleActivity?.agent.id],
    ];
    setFilterHistory(newDomain);
    if (reservation) view.value = "reservation";
  };

  const handleViewHistory = () => {
    const newDomain = [["shop", "=", parseInt(session.shop)]];
    setFilterHistory(newDomain);
    view.value = "history";
  };

  async function handleFilterHistory(newDomain) {
    setFilterHistory(newDomain);
  }

  const resetAndSetShop = async (dateValue = filterDay.value) => {
    const reset = await resetNewRecord();
    if (reset) {
      reset.toStore.shipment_date = date.format(dateValue, "YYYY-MM-DD");
      reset.toRecord.shipment_date = date.format(dateValue, "YYYY-MM-DD");
      updateStore(reset.toStore);
      updateRecord(reset.toRecord);
    }
  };

  const resetNewRecord = async () => {
    const sale = await getSalesForUser();

    if (!sale) {
      const agent = saleActivity?.agent;
      const party = saleActivity?.party;
      const sale_device = saleActivity?.sale_device;
      const shop = { id: saleActivity?.shop, name: saleActivity?.shop_name };
      const nextId = -Date.now();

      const [toStore, toRecord] = await funcs.getDefaults(nextId, ctxView);
      toStore.agent = agent?.id;
      toRecord.agent = agent;
      toStore.party = party?.id;
      toRecord.party = party;
      toStore.sale_device = sale_device?.id;
      toRecord.sale_device = sale_device;
      toStore.shop = shop?.id;
      toRecord.shop = shop;
      toStore.warehouse = saleActivity?.warehouse;

      return { toStore, toRecord };
    }

    return null;
  };

  const getSalesForUser = async () => {
    const filterDayStart = date.format(filterDay.value, "YYYY-MM-DD");
    const dom = [
      ["create_uid", "=", session.user],
      ["state", "=", "draft"],
      ["kind", "!=", "reservation"],
      ["sale_date", "=", filterDayStart],
      ["lines.origin", "ilike", "sale_activity.activity%"],
    ];

    const { data: resSale } = await proxy.search("sale.sale", dom, ["id"]);
    if (resSale.length !== 0) {
      setRecord(resSale[0].id, ctxView.model, ctxView);
      return true;
    }

    return false;
  };

  const onChangeView = (_, action) => {
    if (action === "return") {
      view.value = prevView.value;
      return;
    }
    prevView.value = view.value;
    view.value = action;
  };

  useEffect(() => {
    const init = async () => {
      await resetAndSetShop(filterDay.value);
    };
    init();
  }, [props]);

  return (
    <Fragment>
      {configuration?.date_picker && (
        <CalendarFilter action={handleChangeDate} />
      )}

      {["create", "edit"].includes(view.value) &&
        (record.id < 0 || record.state === "draft") &&
        isLastToday && (
          <ScheduledActivities
            currentDay={filterDay.value}
            ctxView={ctxViewAc}
            view={view.value}
            statusShedule={statusShedule.value}
            configuration={configuration}
          />
        )}

      <div
        className={`flex ${
          statusSection.value ? "flex-row justify-between" : "flex-col"
        }`}
      >
        <div
          className={
            statusSection.value
              ? "md:w-[65vw] xl:w-[70vw] "
              : "md:w-12/12 relative pr-10"
          }
        >
          {["create", "edit"].includes(view.value) ? (
            <QuickForm ctxView={ctxView} level="main" onChangeView={onChangeView} />
          ) : view.value === "history" || view.value === "reservation" ? (
            <div className="py-10 mx-4">
              <BoardHeader
                key="board-header"
                ctxView={newCtxView}
                viewType="list"
                handleFilter={handleFilterHistory}
              />
              <QuickTable
                key="quick-table"
                ctxView={newCtxView}
                onChangeView={onChangeView}
                domain={filterHistory}
              />
            </div>
          ) : (
            <p>Seleccionar algo</p>
          )}
        </div>

        <div className="flex-1">
          {props.permits && (
            <PanelRight
              position="right"
              widgets={["status", "numPeople"]}
              title="INFO ACTIVIDADES"
              bgColor={statusSection.value ? "bg-gray-100" : "bg-sky-950"}
              status={statusSection.value}
              handleChangeStatus={false}
              displayOpen={true}
              style="bg-gray-100 px-2 custom-transition duration-500 sticky w-full"
            >
              <WidgetSideStatusActivity
                title={<FM id="booking.widget.screen.huesped_house" />}
                background="bg-gray-100"
                name="statusHotel"
                day={filterDay}
              />
            </PanelRight>
          )}

          <PanelRight
            position="right"
            widgets={["status", "numPeople"]}
            title=""
            bgColor={statusSection.value ? "bg-gray-100" : "bg-sky-950"}
            status={statusSection.value}
            handleChangeStatus={false}
            displayOpen={true}
            style="bg-gray-100 px-2 custom-transition duration-500 sticky w-full"
          >
            {configuration?.menu_view === "show" && <MenuRest record={[]} />}
            <StdButton
              content="sale.sale.new_sale"
              size="w-full"
              color="green"
              onClick={() => {
                resetAndSetShop(filterDay.value);
                view.value = "create";
                statusShedule.value = true;
              }}
            />
            <StdButton
              content="sale.sale.history"
              size="w-full"
              color="bluePresik"
              onClick={handleViewHistory}
              disabled={view.value === "history"}
            />
            <StdButton
              content="sale.sale.btn_reservation"
              size="w-full"
              color="black"
              onClick={() => handleViewReservation(true)}
              disabled={view.value === "reservation"}
            />
          </PanelRight>
        </div>
      </div>
    </Fragment>
  );
};

export default ScreenActivity;
